import { feedbacks, feedbackTagMappings, feedbackTags } from '@markku/db'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'

// Base schemas
export const selectFeedbackSchema = createSelectSchema(feedbacks)
export const selectFeedbackTagSchema = createSelectSchema(feedbackTags)
export const selectFeedbackTagMappingSchema =
  createSelectSchema(feedbackTagMappings)

// Insert schema for feedback (without auto-generated fields)
export const insertFeedbackSchema = createInsertSchema(feedbacks).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdByUserId: true, // Will be set from context
})

// Create feedback schema with tags
export const createFeedbackSchema = z.object({
  visitId: z.number(),
  ratingValue: z.number().min(1).max(5),
  description: z.string().optional().nullable(),
  tagIds: z
    .array(
      z.object({
        feedbackTagId: z.number(),
      }),
    )
    .optional(),
})

// Update feedback schema
export const updateFeedbackSchema = z.object({
  ratingValue: z.number().min(1).max(5).optional(),
  description: z.string().optional().nullable(),
  tagIds: z
    .array(
      z.object({
        feedbackTagId: z.number(),
      }),
    )
    .optional(),
})

// Feedback with relations
export const selectFeedbackWithRelationsSchema = selectFeedbackSchema.extend({
  visit: z
    .object({
      id: z.number(),
      title: z.string().nullable(),
      // Nullable since the multi-day refactor: 'unscheduled' visits have no
      // times yet. Feedback on such a visit is unusual but type-honestly
      // possible until the visit is dated.
      startTime: z.string().nullable(),
      endTime: z.string().nullable(),
      status: z.enum([
        'unscheduled',
        'scheduled',
        'in_progress',
        'completed',
        'cancelled',
      ]),
    })
    .optional(),
  createdByUser: z
    .object({
      id: z.number(),
      firstName: z.string(),
      lastName: z.string(),
      email: z.string(),
    })
    .optional(),
  feedbackTagMappings: z
    .array(
      selectFeedbackTagMappingSchema.extend({
        feedbackTag: selectFeedbackTagSchema.optional(),
      }),
    )
    .optional(),
})

// Feedback for an attendee (employee viewing their own visit feedback).
// Adds property/address so the UI can show where the visit took place.
export const selectFeedbackForAttendeeSchema = selectFeedbackSchema.extend({
  visit: z
    .object({
      id: z.number(),
      title: z.string().nullable(),
      startTime: z.string().nullable(),
      endTime: z.string().nullable(),
      property: z
        .object({
          id: z.number(),
          address: z
            .object({
              street: z.string(),
              city: z.string(),
            })
            .nullable()
            .optional(),
        })
        .nullable()
        .optional(),
    })
    .optional(),
  feedbackTagMappings: z
    .array(
      selectFeedbackTagMappingSchema.extend({
        feedbackTag: selectFeedbackTagSchema.optional(),
      }),
    )
    .optional(),
})

export const listFeedbackForAttendeeInputSchema = z.object({
  monthsBack: z.number().int().min(1).max(60).default(6),
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(1000).default(50),
})

export const listFeedbackForAttendeeResponseSchema = z.object({
  items: z.array(selectFeedbackForAttendeeSchema),
  totalCount: z.number(),
})

export const statsForAttendeeInputSchema = z.object({
  monthsBack: z.number().int().min(1).max(60).default(6),
})

export const statsForAttendeeResponseSchema = z.object({
  avgRating: z.number().nullable(),
  count: z.number(),
  monthsBack: z.number(),
})

// CSAT for the company portal — rolling-window satisfaction aggregates.
export const csatForPropertyInputSchema = z.object({
  propertyId: z.number(),
  monthsBack: z.number().int().min(1).max(60).default(12),
})

export const csatForPropertyResponseSchema = z.object({
  avgRating: z.number().nullable(),
  count: z.number(),
  monthsBack: z.number(),
})

export const csatForCustomerInputSchema = z.object({
  customerId: z.number(),
  monthsBack: z.number().int().min(1).max(60).default(12),
})

const csatPropertyStatSchema = z.object({
  propertyId: z.number(),
  avgRating: z.number().nullable(),
  count: z.number(),
})

export const csatForCustomerResponseSchema = z.object({
  // Volume-weighted average of all ratings across the customership.
  portfolio: z.object({
    avgRating: z.number().nullable(),
    count: z.number(),
  }),
  // Per-property breakdown — properties with no in-window ratings are omitted.
  byProperty: z.array(csatPropertyStatSchema),
  monthsBack: z.number(),
})

// Type exports
export type Feedback = z.infer<typeof selectFeedbackSchema>
export type FeedbackTag = z.infer<typeof selectFeedbackTagSchema>
export type FeedbackTagMapping = z.infer<typeof selectFeedbackTagMappingSchema>
export type FeedbackWithRelations = z.infer<
  typeof selectFeedbackWithRelationsSchema
>
export type CreateFeedback = z.infer<typeof createFeedbackSchema>
export type UpdateFeedback = z.infer<typeof updateFeedbackSchema>
export type FeedbackForAttendee = z.infer<
  typeof selectFeedbackForAttendeeSchema
>
export type ListFeedbackForAttendeeInput = z.infer<
  typeof listFeedbackForAttendeeInputSchema
>
export type ListFeedbackForAttendeeResponse = z.infer<
  typeof listFeedbackForAttendeeResponseSchema
>
export type StatsForAttendeeInput = z.infer<typeof statsForAttendeeInputSchema>
export type StatsForAttendeeResponse = z.infer<
  typeof statsForAttendeeResponseSchema
>
export type CsatForPropertyInput = z.infer<typeof csatForPropertyInputSchema>
export type CsatForPropertyResponse = z.infer<
  typeof csatForPropertyResponseSchema
>
export type CsatForCustomerInput = z.infer<typeof csatForCustomerInputSchema>
export type CsatForCustomerResponse = z.infer<
  typeof csatForCustomerResponseSchema
>
